import React from "react"
import {useTranslation} from "react-i18next"

// Components
import Icon from "../../../../../components/modern/Icon"
import ModernTooltip from "../../../../../components/modern/Tooltip"

// Constants

// Models
import {AvailableRecordType} from "../../../models"

// Utils
import {shortRecordName} from "../../../../../utils/ModernFormat"
import {
    COLS,
    SortKey,
    SortState,
    TabKey,
    countryNameKey,
    formatRecordTime,
    hasResult,
    heartSeverity,
    recordAge,
    recordHeartRate,
    resultMeta,
} from "./columns"
import {
    hasRecordAction,
    isInProgressRecord,
    isRecordActionDisabled,
    recordActionKind,
} from "../../../utils/recordActions"

// Styles
import useStyles from "./styles"

const TABS: Array<{key: TabKey, labelKey: string}> = [
    {key: "all", labelKey: "modern.tab_all"},
    {key: "today", labelKey: "modern.tab_today"},
    {key: "week", labelKey: "modern.tab_week"},
];

const COPY_FEEDBACK_MS = 1600;
const SENTINEL_MARGIN = 300;

type CopyState = {id: string, ok: boolean}

type ModernRecordsCardProps = {
    /** Already filtered + sorted by the page — the page needs the same list for the Enter shortcut. */
    rows: Array<AvailableRecordType>
    /** How many rows have been accumulated from the server so far. */
    loadedCount: number
    /** `availableRecordsTotal` — the server-side count, which exceeds `rows` until everything is loaded. */
    total: number
    loading: boolean
    done: boolean
    /** The last page fetch failed — the list is incomplete. */
    error: boolean
    loadMore(): void
    retry(): void
    pendingCount: number
    onApplyPending(): void
    tab: TabKey
    onTabChange(tab: TabKey): void
    query: string
    onQueryChange(query: string): void
    searchRef: React.RefObject<HTMLInputElement>
    sort: SortState
    onSortChange(key: SortKey): void
    onClearFilters(): void
    inProgressRecord: AvailableRecordType | null
    onOpenRecord(record: AvailableRecordType): void
}

/**
 * The Modern "Available records" card: tabs, search, sortable columns, the
 * result pill, copy-record-name, the per-row Review/Continue action, the empty
 * state, infinite scroll and the footer count.
 *
 * Purely presentational apart from two things it must own:
 *
 * 1. The IntersectionObserver sentinel (guarded: CRA 3.3 ships no polyfill and
 *    the embedded Safari targets may not have it, so there is a real
 *    "Load more" button fallback).
 * 2. Clipboard feedback, since this app has no toast host.
 *
 * Doctor-unassign events are NOT handled here any more — they arrive as a
 * websocket event that the ws epic turns into a refetch, for both skins at
 * once. See ../../../../ws/store/epics.
 *
 * @param props
 */
export default function ModernRecordsCard(props: ModernRecordsCardProps) {
    const styles = useStyles();
    const [t] = useTranslation("common");
    const {
        rows,
        loadedCount,
        total,
        loading,
        done,
        error,
        loadMore,
        retry,
        pendingCount,
        onApplyPending,
        tab,
        onTabChange,
        query,
        onQueryChange,
        searchRef,
        sort,
        onSortChange,
        onClearFilters,
        inProgressRecord,
        onOpenRecord,
    } = props;

    const sentinelRef = React.useRef<HTMLDivElement>(null);
    const copyTimerRef = React.useRef<number | null>(null);
    // `navigator.clipboard.writeText` resolves asynchronously and cannot be
    // cancelled, so a row click (or a skin flip) between the click and the
    // resolution would otherwise setState on an unmounted component.
    const mountedRef = React.useRef(true);
    const [copyState, setCopyState] = React.useState<CopyState | null>(null);
    const [atBottom, setAtBottom] = React.useState(false);

    /*
     * There WAS a `window.location.reload()` here, fired when the mutable
     * module flag `myInitObject.data` read "true" — the doctor being unassigned
     * from a record. Its effect dependency was that same mutable property, which
     * never changes identity, so the reload went off on whatever unrelated
     * render came next and read as the page crashing. `doctorUnassignedWSEvent`
     * now refetches the queue through the websocket epic instead, which is what
     * a reload was approximating. Classic's <AvailableRecordTable/> lost the
     * same block.
     */

    const supportsObserver = typeof window !== "undefined"
      && typeof (window as any).IntersectionObserver !== "undefined";

    React.useEffect(() => {
        const el = sentinelRef.current;

        if (false === supportsObserver || el === null) {
            return () => undefined
        }

        const observer = new IntersectionObserver(
          entries => setAtBottom(entries.length > 0 && entries[0].isIntersecting),
          {rootMargin: `${SENTINEL_MARGIN}px`},
        );

        observer.observe(el);

        return () => observer.disconnect()
    }, [supportsObserver]);

    // Level-triggered, exactly as the prototype: re-checked after every page
    // settles, so a short viewport cannot leave the list stuck mid-scroll.
    React.useEffect(() => {
        // `error` gates this too: without it the sentinel would re-fire
        // loadMore() on every render for as long as the failed page stayed
        // unfetched.
        if (false === atBottom || loading || done || error) {
            return
        }

        const el = sentinelRef.current;
        if (el !== null && el.getBoundingClientRect().top > window.innerHeight + SENTINEL_MARGIN) {
            return
        }

        loadMore()
    }, [atBottom, loading, done, error, loadedCount, loadMore]);

    React.useEffect(() => {
        mountedRef.current = true;

        return () => {
            mountedRef.current = false;

            if (copyTimerRef.current !== null) {
                window.clearTimeout(copyTimerRef.current)
            }
        }
    }, []);

    const flashCopy = React.useCallback((id: string, ok: boolean) => {
        if (false === mountedRef.current) {
            return
        }

        if (copyTimerRef.current !== null) {
            window.clearTimeout(copyTimerRef.current)
        }

        setCopyState({id: id, ok: ok});
        copyTimerRef.current = window.setTimeout(() => {
            copyTimerRef.current = null;
            setCopyState(null)
        }, COPY_FEEDBACK_MS)
    }, []);

    const handleCopy = React.useCallback((record: AvailableRecordType, e: React.MouseEvent) => {
        e.stopPropagation();

        const clipboard = navigator.clipboard;

        // Not guaranteed: absent on insecure origins and on the older embedded
        // Safari builds in the target matrix.
        if (!clipboard || typeof clipboard.writeText !== "function") {
            flashCopy(record.teleRecordId, false);

            return
        }

        clipboard.writeText(record.fileName || "")
          .then(() => flashCopy(record.teleRecordId, true))
          .catch(() => flashCopy(record.teleRecordId, false))
    }, [flashCopy]);

    return (
      <section className={styles.card} aria-label={t("modern.available_records")}>
          <div className={styles.card_head}>
              <h2 className={styles.card_title}>{t("modern.available_records")}</h2>

              <div className={styles.tabs} role="tablist">
                  {TABS.map(item => (
                    <button
                      key={item.key}
                      type="button"
                      role="tab"
                      aria-selected={tab === item.key}
                      className={`${styles.tab_btn} ${tab === item.key ? styles.tab_btn__active : ""}`}
                      onClick={() => onTabChange(item.key)}
                    >
                        {t(item.labelKey)}
                    </button>
                  ))}
              </div>

              <div className={styles.search}>
                  <Icon name="mag" className={styles.search_icon}/>
                  <input
                    ref={searchRef}
                    type="text"
                    className={styles.search_input}
                    value={query}
                    onChange={e => onQueryChange(e.target.value)}
                    placeholder={t("modern.search_placeholder")}
                    aria-label={t("modern.search_placeholder")}
                  />
              </div>
          </div>

          {pendingCount > 0 && (
            <div className={styles.newbar}>
                <button type="button" className={styles.newbtn} onClick={onApplyPending}>
                    <Icon name="arrow" className={styles.newbtn_icon}/>
                    {t("modern.new_records", {count: pendingCount})}
                </button>
            </div>
          )}

          <div className={styles.tablewrap}>
              <table className={styles.table}>
                  <thead>
                  <tr>
                      {COLS.map(col => {
                          const isActive = col.sortable && sort.key === col.key;

                          return (
                            <th
                              key={col.key}
                              data-sortable={col.sortable ? "true" : "false"}
                              data-align={col.alignRight ? "right" : "left"}
                              aria-sort={isActive ? (sort.dir === 1 ? "ascending" : "descending") : undefined}
                              tabIndex={col.sortable ? 0 : undefined}
                              onClick={col.sortable ? () => onSortChange(col.key as SortKey) : undefined}
                              onKeyDown={col.sortable ? (e: React.KeyboardEvent) => {
                                  if (e.key !== "Enter" && e.key !== " ") {
                                      return
                                  }

                                  // Scrolls the page otherwise, and Enter would
                                  // additionally reach the page-level shortcut
                                  // listener on `window` and open a record.
                                  e.preventDefault();
                                  e.stopPropagation();
                                  onSortChange(col.key as SortKey)
                              } : undefined}
                            >
                                {t(col.labelKey)}
                                {col.sortable && (
                                  <span className={`${styles.sorticon} ${isActive ? styles.sorticon__active : ""}`}>
                                      <Icon name="funnel" className={styles.sortfunnel}/>
                                      {isActive ? (sort.dir === 1 ? "▴" : "▾") : ""}
                                  </span>
                                )}
                            </th>
                          )
                      })}
                  </tr>
                  </thead>
                  <tbody>
                  {rows.map(record => {
                      const meta = resultMeta(record);
                      const resultClass = meta.severity === "ok" ? styles.res__ok : styles.res__warn;
                      const actionable = hasRecordAction(record);
                      const disabled = isRecordActionDisabled(record, inProgressRecord);
                      const current = isInProgressRecord(record, inProgressRecord);
                      const kind = recordActionKind(record);
                      const copied = copyState !== null && copyState.id === record.teleRecordId && copyState.ok;
                      const age = recordAge(record);
                      const hr = recordHeartRate(record);
                      const heartClass = heartSeverity(record) === "bad" ? styles.heart__bad : styles.heart__good;

                      return (
                        <tr key={record.teleRecordId} aria-current={current ? "true" : undefined} onDoubleClick={() => {
                            if (actionable && false === disabled) {
                                onOpenRecord(record)
                            }
                        }}>
                            <td className={styles.td_dt}>{formatRecordTime(record)}</td>

                            <td>
                                {false === hasResult(meta) ? "—" : (
                                  <ModernTooltip text={meta.title}>
                                      <span className={`${styles.res} ${resultClass}`}>
                                          <i className={styles.dot}/>
                                          <span className={styles.res_text}>
                                              {meta.label === null ? t(meta.textKey as string) : meta.label}
                                          </span>
                                      </span>
                                  </ModernTooltip>
                                )}
                            </td>

                            <td className={styles.td_pt}>
                                <Icon name="person" className={styles.cellico}/>
                                {age === null ? "—" : age}
                            </td>

                            <td className={styles.td_hr}>
                                <Icon
                                  name="heart"
                                  className={`${styles.heart} ${heartClass}`}
                                />
                                <b>{hr === null ? "—" : hr}</b>
                                {hr === null ? null : <small>bpm</small>}
                            </td>

                            <td className={styles.td_rec}>
                                <Icon name="file" className={styles.cellico}/>
                                <span title={record.fileName}>{shortRecordName(record.fileName)}</span>
                                <button
                                  type="button"
                                  className={`${styles.copy} ${copied ? styles.copy__done : ""}`}
                                  onClick={e => handleCopy(record, e)}
                                  title={t("modern.copy_record_name")}
                                  aria-label={t("modern.copy_record_name")}
                                >
                                    <Icon name={copied ? "check" : "copy"}/>
                                </button>
                            </td>

                            <td
                              className={styles.td_cc}
                              title={
                                  countryNameKey(record.folder) === null
                                    ? (record.folder || undefined)
                                    : t(countryNameKey(record.folder) as string)
                              }
                            >
                                {record.folder || "—"}
                            </td>

                            <td className={styles.td_act}>
                                {actionable && (
                                  <button
                                    type="button"
                                    className={`${styles.open} ${kind === "continue" ? styles.open__primary : ""}`}
                                    disabled={disabled}
                                    onClick={() => onOpenRecord(record)}
                                  >
                                      <Icon name={kind === "continue" ? "play" : "eye"}/>
                                      {kind === "continue" ? t("modern.continue") : t("modern.review")}
                                  </button>
                                )}
                            </td>
                        </tr>
                      )
                  })}
                  </tbody>
              </table>

              {/*
                * Takes precedence over the empty state: with a failed fetch we
                * do not know whether the doctor has no records or simply could
                * not be told about them, and "No available records" is the one
                * answer we must not guess at.
                */}
              {error && false === loading && (
                <div className={styles.empty}>
                    <b className={styles.empty_title}>{t("modern.records_error")}</b>
                    {t("modern.records_error_hint")}{" "}
                    <button
                      type="button"
                      className={`${styles.open} ${styles.empty_inline_btn}`}
                      onClick={retry}
                    >
                        {t("modern.retry")}
                    </button>
                </div>
              )}

              {false === error && rows.length === 0 && false === loading && (
                <div className={styles.empty}>
                    {loadedCount === 0 ? (
                      // Nothing was loaded at all — telling the doctor to adjust
                      // filters that are not hiding anything would be a lie.
                      <b className={styles.empty_title}>
                          {t("stats.available_records_table.no_data")}
                      </b>
                    ) : (
                      <React.Fragment>
                          <b className={styles.empty_title}>{t("modern.no_records_match")}</b>
                          {t("modern.adjust_filters")}{" "}
                          <button
                            type="button"
                            className={`${styles.open} ${styles.empty_inline_btn}`}
                            onClick={onClearFilters}
                          >
                              {t("modern.clear_filters")}
                          </button>
                      </React.Fragment>
                    )}
                </div>
              )}

              {loading && <div className={styles.loadmore}>{t("modern.loading")}</div>}

              {false === supportsObserver && false === done && false === loading && false === error && (
                <div className={styles.loadmore}>
                    <button type="button" className={styles.open} onClick={loadMore}>
                        {t("modern.load_more")}
                    </button>
                </div>
              )}

              <div ref={sentinelRef} className={styles.sentinel} aria-hidden="true"/>
          </div>

          <div className={styles.card_foot}>
              <span className={styles.count}>
                  {`${rows.length} / ${total} `}
                  {t("modern.records_shown", {count: rows.length})}
              </span>
          </div>

          <span className={styles.live_region} role="status" aria-live="polite">
              {copyState === null ? "" : t(copyState.ok ? "modern.copied" : "modern.copy_failed")}
          </span>
      </section>
    )
}
